import type { FC } from 'react';
import { useCallback, useState, useMemo, useEffect, useRef } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { useInstantSearch } from 'react-instantsearch-hooks-web';
import { Button, CircularProgress, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { usePoiFilterOptions } from '../../../map-data/use-filter-options';
import { useMapData } from '../../../map-data/use-map-data';
import type { LcpnTheme } from '../../../styles/theme';
import { Icon } from '../../Icon';
import { PoiSearch } from '../../PoiSearch';
import { PoiSearchResults } from '../../PoiSearch/PoiSearchResults';
import type { PointOfInterestRecord } from '../../../map-types';
import { PoiResultItem } from '../../PoiSearch/PoiResultItem';

import { PickerSelect } from './PickerSelect';
import { ConfirmedSummaryCard } from './ConfirmedSummaryCard';

interface PickerFormState {
  country: string;
  city: string;
  studyId: string;
}

interface PickerProps {
  selectionNumber: 1 | 2;
  initialCountry: string;
  initialCity: string;
  initialStudyId: string;
  initialIsConfirmed?: boolean;
  excludedStudyIds: string[];
  onFormChange: (formState: PickerFormState) => void;
  onRemove?: () => void;
  onConfirm?: (confirmed: boolean) => void;
}

const usePickerStyles = createUseStyles((theme: LcpnTheme) => ({
  pickerContainer: {
    background: theme.colors.white,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: theme.spacing(92),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(6),
  },
  picker: {
    background: theme.colors['blue-100'],
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    padding: theme.spacing(8, 0),
    width: '100%',
  },
  pickerActionText: {
    paddingTop: theme.spacing(3),
    color: theme.colors.white,
    fontSize: theme.fontSizes.h4,
  },
  selectorHeader: {
    background: theme.colors['blue-100'],
    minHeight: theme.spacing(10),
    display: 'flex',
    alignItems: 'center',
    color: theme.colors.white,
    padding: theme.spacing(0, 3),
    fontSize: theme.fontSizes.p2,
    justifyContent: 'space-between',
  },
  selectorContainer: {
    padding: theme.spacing(3),
    position: 'relative',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  searchWrapper: {
    minHeight: theme.spacing(10),
  },
  dropDownsWrapper: {
    background: theme.colors['grey-100'],
    marginTop: theme.spacing(3),
    padding: theme.spacing(3),
    boxSizing: 'border-box',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    '& > div:not(:first-child)': {
      paddingTop: theme.spacing(3),
    },
  },
  clearButton: {
    alignSelf: 'flex-end',
  },
}));

const PoiSearchResultsList: FC<{
  onItemClick: (record: PointOfInterestRecord) => void;
}> = ({ onItemClick }) => {
  const { indexUiState, setIndexUiState } = useInstantSearch();

  return (
    <PoiSearchResults
      resultItemComponent={props => (
        <PoiResultItem
          {...props}
          onResultClick={props => {
            onItemClick(props);
            setIndexUiState({
              ...indexUiState,
              query: undefined,
            });
          }}
        />
      )}
    />
  );
};

export const PoiPicker: FC<PickerProps> = ({
  selectionNumber,
  initialCountry,
  initialCity,
  initialStudyId,
  initialIsConfirmed = false,
  excludedStudyIds,
  onFormChange,
  onRemove,
  onConfirm,
}) => {
  const classes = usePickerStyles();
  const theme = useTheme<LcpnTheme>();
  const { t } = useTranslation();
  const { filterConfig: poiFilterDataOptions } = usePoiFilterOptions();
  const { poiStudyData, studiesById, poiCityMetaData } = useMapData();
  const [selectedCountry, setSelectedCountry] = useState(initialCountry);
  const [selectedCity, setSelectedCity] = useState(initialCity);
  const [selectedStudy, setSelectedStudy] = useState(initialStudyId);
  const [isConfirmed, setIsConfirmed] = useState(initialIsConfirmed);

  // Always start with form open
  const [isPickingStudy, setIsPickingStudy] = useState(true);

  // Update parent whenever form state changes
  const prevFormStateRef = useRef({
    country: '',
    city: '',
    studyId: '',
  });

  useEffect(() => {
    const currentState = {
      country: selectedCountry,
      city: selectedCity,
      studyId: selectedStudy,
    };

    // Only call onFormChange if something actually changed
    if (
      prevFormStateRef.current.country !== currentState.country ||
      prevFormStateRef.current.city !== currentState.city ||
      prevFormStateRef.current.studyId !== currentState.studyId
    ) {
      prevFormStateRef.current = currentState;
      onFormChange(currentState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry, selectedCity, selectedStudy]);

  const filteredCountries = useMemo(() => {
    if (
      !poiFilterDataOptions?.countries ||
      !poiStudyData ||
      !studiesById ||
      !poiCityMetaData
    )
      return [];

    // Filter out countries that have no available studies
    return poiFilterDataOptions.countries.filter(countryName => {
      // Get all studies in this country
      const studiesInCountry = poiStudyData.studies
        .filter(study => study.country === countryName)
        .map(study => study.id);

      // Check if any study in this country is not excluded
      const hasAvailableStudies = studiesInCountry.some(
        studyId => !excludedStudyIds.includes(studyId),
      );

      return hasAvailableStudies;
    });
  }, [
    poiFilterDataOptions,
    poiStudyData,
    studiesById,
    poiCityMetaData,
    excludedStudyIds,
  ]);

  const filteredCities = useMemo(() => {
    if (!selectedCountry || !poiStudyData || !poiCityMetaData || !studiesById)
      return [];

    // Get all cities in the selected country
    const citiesInCountry = [
      ...new Set(
        poiStudyData.studies
          .filter(study => study.country === selectedCountry)
          .map(study => study.city),
      ),
    ];

    // Filter out cities that only have excluded studies
    return citiesInCountry
      .filter(city => {
        const studiesInCity =
          poiCityMetaData[`${city}::${selectedCountry}`] || [];
        const availableStudies = studiesInCity.filter(studyId => {
          const study = studiesById[studyId];
          return (
            study &&
            study.country === selectedCountry &&
            !excludedStudyIds.includes(studyId)
          );
        });
        return availableStudies.length > 0;
      })
      .map(city => ({ label: city, value: city }));
  }, [
    poiStudyData,
    poiCityMetaData,
    studiesById,
    selectedCountry,
    excludedStudyIds,
  ]);

  const filteredStudies = useMemo(() => {
    if (!selectedCity || !studiesById || !poiCityMetaData) return [];

    return (poiCityMetaData[`${selectedCity}::${selectedCountry}`] || [])
      .filter(cityStudyId => {
        const study = studiesById[cityStudyId];
        // Filter out studies that don't exist or don't match country
        return study && study.country === selectedCountry;
      })
      .filter(
        // filter out studies that are selected in other pickers
        cityStudyId => !excludedStudyIds.includes(cityStudyId),
      )
      .map(cityStudyId => {
        const study = studiesById[cityStudyId];
        return {
          label: `${cityStudyId} - ${study?.studyName || 'Unknown'}`,
          value: cityStudyId,
        };
      });
  }, [
    poiCityMetaData,
    selectedCity,
    selectedCountry,
    studiesById,
    excludedStudyIds,
  ]);

  useEffect(() => {
    if (filteredCities.length === 1) {
      setSelectedCity(filteredCities[0]?.value);
    }
  }, [filteredCities]);

  useEffect(() => {
    if (filteredStudies.length === 1) {
      setSelectedStudy(filteredStudies[0].value);
    }
  }, [filteredStudies]);

  useEffect(() => {
    // Notify parent of confirmation state changes
    if (onConfirm) {
      onConfirm(isConfirmed && !!selectedStudy);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConfirmed, selectedStudy]);

  const handleConfirm = useCallback(() => {
    if (selectedCountry && selectedCity && selectedStudy) {
      setIsConfirmed(true);
    }
  }, [selectedCountry, selectedCity, selectedStudy]);

  const handleEdit = useCallback(() => {
    setIsConfirmed(false);
  }, []);

  const handleClearAll = useCallback(() => {
    setSelectedCountry('');
    setSelectedCity('');
    setSelectedStudy('');
  }, []);

  const isFormValid = selectedCountry && selectedCity && selectedStudy;

  if (!isPickingStudy) {
    return (
      <div className={classes.pickerContainer}>
        <button
          className={classes.picker}
          onClick={() => setIsPickingStudy(true)}
        >
          <Icon variant="Add" width={64} height={64} />
          <p className={classes.pickerActionText}>
            {t('comparisonPicker.poi.picker.addEntry')}
          </p>
        </button>
      </div>
    );
  }

  if (!poiFilterDataOptions || !poiStudyData || !studiesById) {
    return (
      <div className={classes.pickerContainer}>
        <div className={classes.selectorHeader}>
          <p>{t('comparisonPicker.poi.picker.title', { selectionNumber })}</p>
        </div>
        <div className={classes.loadingContainer}>
          <CircularProgress size={60} disableShrink />
        </div>
      </div>
    );
  }

  // Render confirmed summary card
  if (isConfirmed && selectedStudy && studiesById) {
    const study = studiesById[selectedStudy];

    // If study no longer exists (e.g., removed from data but still in localStorage), reset
    if (!study) {
      setIsConfirmed(false);
      setSelectedStudy('');
      return null;
    }

    return (
      <ConfirmedSummaryCard
        code={selectedStudy}
        acronym={study.studyAcronym}
        title={study.studyName}
        details={[
          { label: 'Country', value: selectedCountry },
          { label: 'City', value: selectedCity },
        ]}
        headerTitle={t('comparisonPicker.poi.picker.title', {
          selectionNumber,
        })}
        onEdit={handleEdit}
        onRemove={onRemove}
        design={study.design}
      />
    );
  }

  // Render form mode
  return (
    <div className={classes.pickerContainer}>
      <div className={classes.selectorHeader}>
        <p>{t('comparisonPicker.poi.picker.title', { selectionNumber })}</p>
        {onRemove && (
          <Button
            variant="text"
            sx={{
              color: 'white',
              fontSize: theme.fontSizes.p3,
              fontFamily: 'Be Vietnam Pro',
              textTransform: 'none',
              textDecoration: 'underline',
            }}
            size="small"
            onClick={onRemove}
          >
            Remove
          </Button>
        )}
      </div>
      <div className={classes.selectorContainer}>
        <div className={classes.searchWrapper}>
          <PoiSearch
            placeholder={t('comparisonPicker.poi.picker.searchPlaceHolder')}
          >
            <PoiSearchResultsList
              onItemClick={record => {
                setSelectedCountry(record.country);
                setSelectedCity(record.city);
                setSelectedStudy(record.id);
              }}
            />
          </PoiSearch>
        </div>
        <div className={classes.dropDownsWrapper}>
          <Stack spacing={2}>
            <Button
              disabled={!selectedCountry}
              variant="text"
              size="small"
              className={classes.clearButton}
              sx={{
                lineHeight: 1,
                fontSize: theme.fontSizes.p3,
                fontFamily: 'Be Vietnam Pro',
                textTransform: 'none',
                textDecoration: 'underline',
                color: theme.colors['blue-100'],
                padding: 0,
                minWidth: 'auto',
              }}
              onClick={handleClearAll}
            >
              {t('comparisonPicker.clearSelection')}
            </Button>
            <PickerSelect
              onUpdate={country => {
                setSelectedCountry(country);
                setSelectedCity('');
                setSelectedStudy('');
              }}
              placeholder={t('comparisonPicker.poi.picker.countryPlaceholder')}
              selectedValue={selectedCountry}
              options={filteredCountries.map(countryName => ({
                label: countryName,
                value: countryName,
              }))}
            />
            <PickerSelect
              onUpdate={city => {
                setSelectedCity(city);
                setSelectedStudy('');
              }}
              placeholder={t('comparisonPicker.poi.picker.cityPlaceholder')}
              selectedValue={selectedCity}
              options={filteredCities}
              disabled={!selectedCountry}
            />
            <PickerSelect
              onUpdate={studyId => setSelectedStudy(studyId)}
              placeholder={t('comparisonPicker.poi.picker.studyPlaceholder')}
              selectedValue={selectedStudy}
              options={filteredStudies}
              disabled={!selectedCountry || !selectedCity}
            />
            <Button
              onClick={handleConfirm}
              disabled={!isFormValid}
              color={'secondary'}
            >
              Confirm
            </Button>
          </Stack>
        </div>
      </div>
    </div>
  );
};
